import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from "../components/ui/card";
import { AlertTriangle, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

export default function Permise() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openModalId = searchParams.get('modal');

  const permiseOptions = [
    {
      id: 'procura',
      title: 'Eliberare document prin procură',
      desc: 'Eliberarea permisului de conducere prin mandatar cu procură specială.',
      docs: [
        'Procura specială autentificată la notar (în original)',
        'Actul de identitate al mandatarului (original și copie)',
        'Copia actului de identitate al titularului',
        'Cererea tip completată',
        '2 Fotografii color ale titularului (format 35x38.5mm)'
      ]
    },
    {
      id: 'examen_primul',
      title: 'Examen primul permis',
      desc: 'Documentele necesare pentru obținerea primului permis auto.',
      docs: [
        'Cererea tip (se primește și se completează la ghișeu)',
        'Actul de identitate, în original și copie',
        'Dovada plății taxei pentru permisul de conducere (89 lei)',
        'Fișa medicală în original (valabilă 1 an)',
        'Certificatul de cazier judiciar (valabil 6 luni)'
      ]
    },
    {
      id: 'vechi',
      title: 'Preschimbare model vechi',
      desc: 'Înlocuirea permisului de conducere model vechi.',
      docs: [
        'Cererea tip (se primește și se completează la ghișeu)',
        'Actul de identitate, în original și copie',
        'Dovada plății taxei pentru permisul de conducere (89 lei)',
        'Permisul de conducere vechi (în original)',
        'Fișa medicală emisă de o unitate autorizată (dacă permisul este expirat)'
      ]
    },
    {
      id: 'nou',
      title: 'Preschimbare model nou',
      desc: 'Pentru permise pierdute, furate, deteriorate sau modificări de date.',
      docs: [
        'Cererea tip (se primește și se completează la ghișeu)',
        'Actul de identitate, în original și copie',
        'Dovada plății taxei pentru permisul de conducere (89 lei)',
        'Declarație pe proprie răspundere privind pierderea/furtul SAU documentul care atestă schimbarea numelui (după caz)',
        'Fișa medicală (doar dacă se solicită un document cu o nouă valabilitate)'
      ]
    },
    {
      id: 'ue',
      title: 'Actualizare document model U.E.',
      desc: 'Actualizarea documentelor și preschimbarea cu un model U.E.',
      docs: [
        'Cererea tip completată la ghișeu',
        'Actul de identitate, în original și copie',
        'Dovada plății taxei pentru permisul de conducere (89 lei)',
        'Permisul vechi emis de un stat membru U.E. (în original)',
        'Fișa medicală (în funcție de situație)'
      ]
    },
    {
      id: 'strain',
      title: 'Preschimbare permis străin',
      desc: 'Pentru cetățenii care dețin permise emise de state din afara U.E.',
      docs: [
        'Cererea tip completată',
        'Actul de identitate, în original și copie',
        'Permisul de conducere străin (original)',
        'Traducere legalizată a permisului de conducere (după caz)',
        'Dovada plății taxei pentru permisul de conducere (89 lei)',
        'Fișa medicală'
      ]
    }
  ];

  const handleOpenChange = (id, isOpen) => {
    if (isOpen) {
      setSearchParams({ modal: id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <h1 className="text-3xl font-bold text-slate-800 border-b pb-4">Permise de conducere</h1>
      
      <div className="prose prose-slate max-w-none">
        <p className="text-slate-600 text-lg mb-6">
          Permisul de conducere este obținut în urma susținerii unor probe teoretice și practice în urma cărora solicitanții sunt declarați admiși. 
          Faceți click pe oricare dintre situațiile de mai jos pentru a vedea lista completă de documente necesare:
        </p>
        
        <div className="grid md:grid-cols-2 gap-4">
          {permiseOptions.map((opt) => (
            <Dialog key={opt.id} open={openModalId === opt.id} onOpenChange={(isOpen) => handleOpenChange(opt.id, isOpen)}>
              <DialogTrigger asChild>
                <div className={`border rounded-lg p-5 shadow-sm transition-all cursor-pointer group text-left ${openModalId === opt.id ? 'bg-blue-50 border-blue-400' : 'bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}>
                  <h4 className="font-semibold text-blue-700 mb-2 group-hover:text-blue-800 text-lg">{opt.title}</h4>
                  <p className="text-sm text-slate-500">{opt.desc}</p>
                </div>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md bg-white">
                <DialogHeader>
                  <DialogTitle className="text-xl text-blue-900">{opt.title}</DialogTitle>
                  <DialogDescription className="text-slate-500 mt-2">
                    Documentele necesare la ghișeu pentru această operațiune:
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-4">
                  <ul className="space-y-3 m-0 list-none p-0">
                    {opt.docs.map((doc, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 text-sm font-medium">{doc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </DialogContent>
            </Dialog>
          ))}
        </div>

        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-5 flex gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 m-0 leading-relaxed">
            <strong>Atenție:</strong> Fotografia titularului se preia direct la ghișeu în momentul depunerii documentelor. Prezența personală este obligatorie, cu excepția situațiilor în care se depun actele prin mandatar cu procură specială.
          </p>
        </div>
      </div>
    </div>
  );
}
